import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { listWorkflowContentVersions } from "../../../api/content/versionsApi";
import type { VersionLabel } from "../../../api/content/versionDecoders";
import { formatDateTime } from "../../../shared/formatting/dateTimes";
import { useLoad } from "../../../shared/loading/useLoad";
import CopyReference from "../../../ui/content/CopyReference";
import EmptyState from "../../../ui/feedback/EmptyState";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import ChoiceCard from "../../../ui/forms/ChoiceCard";
import FieldGroup from "../../../ui/forms/FieldGroup";
import { versionLabelText } from "../labels/versionText";
import { processTerm } from "../../../api/content/processTerms";
import { classText } from "../../workflows/inputCards/typeWords";

interface WorkflowContentVersionsProps {
  projectId: string;
  /** The Workflow version the list is for, `<item id>@<blob>`. */
  workflow: string;
  /** The chosen content version's reference, `<collection id>@<commit>`; `null` for none yet. */
  chosen: string | null;
  onChoose: (version: VersionLabel) => void;
}

/**
 * The project content's versions that changed something the Workflow reads, newest first
 * (ADR-0033 D5, F29): versions that make no difference to it are left out. Each reads
 * "Project content 1.3.0 · <change line>"; the exact reference is behind the copy button.
 */
export default function WorkflowContentVersions({ projectId, workflow, chosen, onChoose }: WorkflowContentVersionsProps) {
  const { t, i18n } = useTranslation();
  const load = useCallback((signal: AbortSignal) => listWorkflowContentVersions(projectId, workflow, signal), [projectId, workflow]);
  const { state, reload } = useLoad(load);
  return (
    <FieldGroup legend={classText(processTerm("ContentVersion"), t)} className="space-y-2">
      <LoadStatus state={state} loadingLabel={t("versions.contentVersions.loading")} onRetry={reload} compact={true} />
      {state.status === "loaded" && state.value.length === 0 ? <EmptyState size="small">{t("versions.contentVersions.none")}</EmptyState> : null}
      {state.status === "loaded"
        ? state.value.map((version) => (
            <ChoiceCard
              key={version.reference}
              name="content-version"
              checked={version.reference === chosen}
              onChoose={() => {
                onChoose(version);
              }}
              title={versionLabelText(t, null, version.version)}
              description={[version.changeLine, formatDateTime(version.labelledAt, i18n.resolvedLanguage)].filter((part) => part !== "").join(" · ")}
              aside={<CopyReference value={version.reference} />}
            />
          ))
        : null}
    </FieldGroup>
  );
}
